import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type {
  DomainsGeoJSON,
  GridstatsResponse,
  HindcastDetail,
  HindcastSummary,
  TextureDataJSON,
} from "../types";
import type { TextureData } from "weatherlayers-gl";

export function useHindcasts() {
  return useQuery<HindcastSummary[]>({
    queryKey: ["hindcasts"],
    queryFn: () => apiFetch<HindcastSummary[]>("/api/hindcasts"),
    staleTime: 24 * 60 * 60 * 1000,
  });
}

export function useHindcast(name: string | null) {
  return useQuery<HindcastDetail>({
    queryKey: ["hindcast", name],
    queryFn: () => apiFetch<HindcastDetail>(`/api/hindcast/${encodeURIComponent(name!)}`),
    enabled: name != null,
    staleTime: 24 * 60 * 60 * 1000,
  });
}

export function useDomains() {
  return useQuery<DomainsGeoJSON>({
    queryKey: ["domains"],
    queryFn: () => apiFetch<DomainsGeoJSON>("/api/domains"),
    staleTime: 24 * 60 * 60 * 1000,
  });
}

/** Convert a JSON TextureData (null = NaN) to a weatherlayers-gl TextureData with Float32Array. */
export function toTextureData(t: TextureDataJSON): TextureData {
  const float32 = new Float32Array(t.data.map((v) => (v === null ? NaN : v)));
  return { data: float32, width: t.width, height: t.height };
}

/**
 * Convert a 2-channel JSON TextureData (interleaved [U0,V0,U1,V1,...]) to a
 * weatherlayers-gl TextureData.  The Je() format function in weatherlayers-gl
 * detects length = 2 * width * height and creates an rg32float texture,
 * which the ParticleLayer shader reads as (R=U, G=V) velocity.
 */
export function toUVTextureData(t: TextureDataJSON): TextureData {
  const float32 = new Float32Array(t.data.map((v) => (v === null ? NaN : v)));
  return { data: float32, width: t.width, height: t.height };
}

export function useGridstats(name: string | null) {
  return useQuery<GridstatsResponse>({
    queryKey: ["gridstats", name],
    queryFn: () => apiFetch<GridstatsResponse>(`/api/gridstats/${encodeURIComponent(name!)}`),
    enabled: name != null,
    // gridstats is large — cache for the session, it never changes
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
